import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Bell, BellOff, Sparkles, CheckCheck, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNotifications } from '@/hooks/useNotifications';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import { buildMessageUserParam } from '@/lib/messages/userSlug';
import { cn } from '@/lib/utils';

export function NotificationDropdown() {
  const {
    notifications,
    unreadCount,
    suppressedCount,
    activeSnoozes,
    markAsRead,
    markAllAsRead,
    registerNotificationEngagement,
    snoozeNotificationType,
    clearAllSnoozes,
    isQuietSessionActive,
  } = useNotifications();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [filterMode, setFilterMode] = useState<'all' | 'unread' | 'mentions'>('all');
  const containerRef = useRef<HTMLDivElement | null>(null);

  const filteredNotifications = useMemo(() => {
    if (filterMode === 'unread') {
      return notifications.filter((n) => !n.read);
    }
    if (filterMode === 'mentions') {
      return notifications.filter(
        (n) =>
          n.type === 'post_mention' ||
          n.type === 'comment_reply' ||
          n.type === 'new_comment'
      );
    }
    return notifications;
  }, [notifications, filterMode]);

  React.useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const handleNotificationClick = (notification: any, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    markAsRead(notification.id);
    registerNotificationEngagement(notification.type, 'open');
    setOpen(false);
    
    // Extract navigation data
    const data = notification.data || {};
    const coerceString = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : undefined);
    const postId = coerceString(data.postId);
    const commentId = coerceString(data.commentId);
    const senderId = coerceString(data.senderId);
    const accepterId = coerceString(data.accepterId);
    const callId = coerceString(data.callId);
    const actionUrl = coerceString(data.actionUrl);
    
    // Navigate based on notification type with specific content focus
    switch (notification.type) {
      case 'new_reaction':
      case 'new_comment':
      case 'comment_reply':
      case 'post_mention':
      case 'post_share':
      case 'new_post':
      case 'profile_post':
        // Navigate to the specific post
        if (postId) {
          const qs = commentId ? `?comment=${encodeURIComponent(commentId)}` : '';
          navigate(`/post/${encodeURIComponent(postId)}${qs}`, { replace: false });
        } else {
          // Fallback to profile if we have the author
          const profileUsername = data.profileOwnerUsername || data.authorUsername;
          if (profileUsername) {
            navigate(`/profile/${profileUsername}`, { replace: false });
          } else {
            navigate('/feed', { replace: false });
          }
        }
        break;
        
      case 'new_message':
        // Navigate to messages with specific sender opened
        if (senderId) {
              const senderSlug = buildMessageUserParam(data.senderUsername, senderId);
              navigate(`/messages?user=${encodeURIComponent(senderSlug)}`, { replace: false });
        } else {
          navigate('/messages', { replace: false });
        }
        break;
        
      case 'friend_request':
      case 'friend_accepted':
        // Navigate to the person's profile
        const userId = accepterId || senderId;
        const username = data.accepterUsername || data.senderUsername;
        if (username) {
          navigate(`/profile/${username}`, { replace: false });
        } else if (userId) {
          navigate(`/profile/${userId}`, { replace: false });
        } else {
          navigate('/friends', { replace: false });
        }
        break;
        
      case 'call':
        // Prefer actionUrl when provided (newer notifications); normalize /calls/:id to /calls?call=:id.
        if (actionUrl && actionUrl.trim()) {
          const url = actionUrl.trim();
          if (url.startsWith('/calls/')) {
            const id = url.split('/calls/')[1]?.split(/[?#]/)[0];
            if (id) {
              navigate(`/calls?call=${encodeURIComponent(id)}`, { replace: false });
              break;
            }
          }
          if (url.startsWith('/calls')) {
            navigate('/calls', { replace: false });
            break;
          }
          navigate(url, { replace: false });
        } else {
          if (callId) {
            navigate(`/calls?call=${encodeURIComponent(callId)}`, { replace: false });
          } else {
            navigate('/calls', { replace: false });
          }
        }
        break;
        
      case 'achievement':
        // Navigate to user's own profile to see achievements
        navigate('/profile', { replace: false });
        break;
        
      case 'system':
        // Check if there's an action URL
        if (data.actionUrl) {
          navigate(data.actionUrl, { replace: false });
        }
        break;
        
      default:
        // Fallback to feed
        navigate('/feed', { replace: false });
        break;
    }
  };

  // Handle "View All Notifications" click
  const handleViewAllClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen(false);
    navigate('/notifications', { replace: false });
  };

  const getNotificationText = (notification: any) => {
    const data = notification.data || {};
    
    switch (notification.type) {
      case 'new_reaction':
        return `${data.reactorName || 'Someone'} ${data.reactionType || 'reacted to'} your ${data.postMediaType === 'video' ? 'video' : data.postMediaType === 'image' ? 'photo' : 'post'}`;
      case 'new_comment':
        const commentPreview = data.commentText ? `: "${data.commentText.substring(0, 30)}${data.commentText.length > 30 ? '...' : ''}"` : '';
        return `${data.commenterName || 'Someone'} commented on your ${data.postMediaType === 'video' ? 'video' : data.postMediaType === 'image' ? 'photo' : 'post'}${commentPreview}`;
      case 'comment_reply':
        return `${data.commenterName || 'Someone'} replied to your comment`;
      case 'post_mention':
        return `${data.authorName || 'Someone'} mentioned you in a post`;
      case 'post_share':
        return `${data.senderName || 'Someone'} shared your post`;
      case 'profile_post':
        return `${data.authorName || 'Someone'} posted on your profile`;
      case 'new_message':
        const messagePreview = data.messagePreview ? `: "${data.messagePreview}"` : '';
        return `${data.senderName || 'Someone'} sent you a message${messagePreview}`;
      case 'friend_request':
        return `${data.senderName || 'Someone'} sent you a friend request`;
      case 'friend_accepted':
        return `${data.accepterName || 'Someone'} accepted your friend request`;
      case 'new_post':
        return `${data.authorName || 'Someone'} shared a new post`;
      case 'call':
        const callType = data.callType === 'video' ? 'video call' : 'voice call';
        return `Missed ${callType} from ${data.senderName || 'Someone'}`;
      case 'achievement':
        return `🎉 You've reached ${data.milestone} ${data.achievementType}!`;
      case 'system':
        return notification.message || 'System notification';
      default:
        return notification.message || 'New notification';
    }
  };

  const getNotificationAvatar = (notification: any) => {
    switch (notification.type) {
      case 'new_reaction':
        return notification.data.reactorPhoto;
      case 'new_comment':
        return notification.data.commenterPhoto;
      case 'new_message':
        return notification.data.senderPhoto;
      case 'friend_request':
        return notification.data.senderPhoto;
      case 'friend_accepted':
        return notification.data.accepterPhoto;
      case 'new_post':
        return notification.data.authorPhoto;
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return 'Just now';
    }
  };

  return (
    <div ref={containerRef} className="relative">
        <button
          type="button"
          className="relative p-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600 dark:text-blue-400"
          aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

      {open && (
        <div className="absolute right-0 mt-2 w-84 sm:w-96 rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900 z-[120] overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <div className="flex flex-col">
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-slate-800 dark:text-slate-200">Notifications</span>
              {isQuietSessionActive && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 flex items-center gap-1">
                  <Moon className="w-2.5 h-2.5" /> Quiet
                </span>
              )}
            </div>
            <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-indigo-500" />
              AI ranked feed
            </span>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  markAllAsRead();
                }}
                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 flex items-center gap-1 font-medium px-2 py-1 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                title="Mark all notifications as read"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                <span>Mark all read</span>
              </button>
            )}
          </div>
        </div>

        {/* Quick Filter Tabs */}
        <div className="flex items-center px-4 py-1.5 border-b border-slate-100 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/50 gap-1 text-xs">
          <button
            type="button"
            onClick={() => setFilterMode('all')}
            className={cn(
              "px-2.5 py-1 rounded-lg font-medium transition-colors",
              filterMode === 'all'
                ? "bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-xs"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            )}
          >
            All ({notifications.length})
          </button>
          <button
            type="button"
            onClick={() => setFilterMode('unread')}
            className={cn(
              "px-2.5 py-1 rounded-lg font-medium transition-colors",
              filterMode === 'unread'
                ? "bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-xs"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            )}
          >
            Unread {unreadCount > 0 && `(${unreadCount})`}
          </button>
          <button
            type="button"
            onClick={() => setFilterMode('mentions')}
            className={cn(
              "px-2.5 py-1 rounded-lg font-medium transition-colors",
              filterMode === 'mentions'
                ? "bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-xs"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            )}
          >
            Mentions
          </button>
        </div>

        {activeSnoozes.length > 0 && (
          <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-600 dark:text-slate-300">{activeSnoozes.length} snoozed type(s)</span>
              <button
                type="button"
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  clearAllSnoozes();
                }}
              >
                Clear snoozes
              </button>
            </div>
          </div>
        )}
        <ScrollArea className="h-[400px]">
          {filteredNotifications.length === 0 ? (
            <div className="p-8 text-center text-slate-500 dark:text-slate-400">
              <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-sm">
                {filterMode === 'unread' ? 'No unread notifications' : filterMode === 'mentions' ? 'No mentions or replies yet' : 'No notifications yet'}
              </p>
            </div>
          ) : (
            filteredNotifications.map((notification) => (
              <React.Fragment key={notification.id}>
                <button
                  type="button"
                  className={`px-4 py-3 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 ${
                    !notification.read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                  } w-full text-left`}
                  onClick={(e) => handleNotificationClick(notification, e)}
                >
                  <div className="flex items-center gap-3 w-full">
                    {getNotificationAvatar(notification) && (
                      <img
                        src={getNotificationAvatar(notification)}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-700 dark:text-slate-300 font-medium">
                        {getNotificationText(notification)}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-2">
                        <span>{formatDate(notification.createdAt)}</span>
                        {typeof notification.priorityScore === 'number' && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-300">
                            score {notification.priorityScore}
                          </span>
                        )}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
                      title="Snooze this type for 60 minutes"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        snoozeNotificationType(notification.type, 60);
                      }}
                    >
                      <BellOff className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                    {!notification.read && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                    )}
                  </div>
                </button>
                {filteredNotifications.indexOf(notification) < filteredNotifications.length - 1 && (
                  <div className="-mx-1 my-1 h-px bg-muted" />
                )}
              </React.Fragment>
            ))
          )}
        </ScrollArea>
        <div className="p-3 border-t border-slate-200 dark:border-slate-700">
          <Button
            variant="ghost"
            className="w-full text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            onClick={(e) => handleViewAllClick(e)}
          >
            View All Notifications
          </Button>
        </div>
      </div>
      )}
    </div>
  );
}